import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { IconX } from "@tabler/icons-react";
import { ProfilingConversation } from "./steps/ProfilingConversation";
import { apiClient } from "../../services/api";
import { displayToast } from "../../components/app/AppToast";
import { getUserHomeRoute } from "../../domains/app/utils/getInitialRoute";
import {
  settleHomeDestination,
  useHomeSurface,
} from "../../domains/app/hooks/useHomeSurface";
import { resetPreferenceConversation } from "../../domains/auth/stores/onboardingConversationStore";
import {
  getCurrentUserData,
  replaceCurrentUserData,
  useCurrentUser,
} from "../../domains/user/hooks/useCurrentUser";
import { useSingleFlightAction } from "../../hooks/useSingleFlightAction";
import { createMemoryStore } from "../../lib/store";
import { useNotificationPermissionState } from "../../domains/notification/hooks/useNotificationPermissionState";
import { PushOptInModal } from "../../components/modals/notification/PushOptInModal";
import {
  isDesktopShell,
  isNativeApp,
} from "../../common/hooks/useIsDesktopView";
import {
  gaEventFeedTasteExplored,
  gaEventPreferenceOnboardingCompleted,
} from "../../tools/analytics/domains/onboarding.analytics";

/**
 * Whether the push ask is currently on screen, between the conversation
 * completing and the user answering it.
 *
 * A memory store rather than component state: this is client state, and it must
 * survive the re-render that `replaceCurrentUserData` triggers without the page
 * owning a state machine. It is deliberately NOT durable — a reload during the
 * ask drops it, which is correct, since the ask belongs to the moment the
 * conversation ended and the `/onboarding/notifications` guard picks up the
 * user later anyway.
 */
const usePushOptInPending = createMemoryStore(false);

/**
 * The profiling conversation as a full PAGE (`/onboarding/interests`), not a
 * sheet — the Explore gate. `AppRoutes` routes every `/discovery*` request here
 * until `hasCompletedFeedProfiling`: everyone walks the conversation once,
 * including a user who already chatted with JuisciGPT and picked up interests
 * along the way (those arrive pre-selected on the topics turn).
 *
 * It gates a SURFACE, so it must not trap the user in it. The page carries no
 * navbar and nothing renders behind it, so with no exit of its own "Explore"
 * was a one-way door — which is exactly how it was reported. The cross is that
 * exit (`close()`): it goes BACK, and it is deliberately not a skip — nothing is
 * stamped, so Explore stays gated and the conversation is offered again the next
 * time the user asks for the feed.
 *
 * The completing way out is the conversation's own "Explore my feed", which
 * lands here in `finish()`. The interests were already written by the topics
 * turn (`PUT /users/me/interests`); what this action adds is the completion
 * stamp — `preference-onboarding/complete`, the durable "walked it" signal the
 * gate reads, shared with the notification-prompt guard in `AppRoutes`
 * (`uppCompletedAt`).
 *
 * Completing it is also where the PUSH ask happens (`PushOptInModal`): the user
 * has just declared the topics they care about, which is the one moment where
 * "I'll notify you when something lands in them" is the follow-up to what they
 * did. The ask is raised AFTER the stamp, so abandoning the OS prompt cannot
 * send them back through the blocking conversation — and the navigation to the
 * feed moves into the modal's `onDone`, which every exit calls.
 */
export default function InterestsProfilingPage(): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { currentUser } = useCurrentUser();
  // Read reactively, in the body: the handler below is redefined every
  // render, so by the time the cross is pressed it closes over the settled
  // value (`.knowledge/growthbook-oneshot-flag-race.md`).
  const homeSurface = useHomeSurface();
  const terminalAction = useSingleFlightAction();
  const { permissionState } = useNotificationPermissionState();
  const [pushOptInPending, setPushOptInPending] = usePushOptInPending();

  // Leaving without finishing. `goBack` is what the user means by the cross:
  // return where they came from — the router reached this page by REPLACING the
  // `/discovery` request, so the previous entry is whatever they were doing
  // before asking for Explore, never the gated route itself.
  //
  // `history.length === 1` means this entry IS the session's first and there is
  // nothing behind it (a `nudge-profile` deep link, a cold boot straight onto
  // the gate, a fresh native webview). Falling back to `getUserHomeRoute` rather
  // than to `/gpt/chat/new` flatly: a room member belongs in their pinned room,
  // and JuisciGPT is already what that returns for everyone else.
  function close(): void {
    if (history.length > 1) {
      history.goBack();
      return;
    }
    history.replace(
      currentUser === null
        ? "/"
        : getUserHomeRoute(settleHomeDestination(homeSurface, currentUser))
    );
  }

  async function finish(): Promise<void> {
    await terminalAction.run(async () => {
      try {
        const { body } = await apiClient.put(
          "/webapp/private/users/me/preference-onboarding/complete",
          {}
        );
        const cachedUser = getCurrentUserData();
        if (cachedUser !== null) {
          replaceCurrentUserData({
            ...cachedUser,
            preferenceOnboarding: body.preferenceOnboarding,
          });
        }
        gaEventFeedTasteExplored({ flow: "preference_refresh" });
        gaEventPreferenceOnboardingCompleted({
          policy_version: body.preferenceOnboarding.policyVersion,
          surface: "discovery",
          eligibility_reason: "profile_missing",
        });
        resetPreferenceConversation();

        // The push ask lands HERE, after the completion stamp: a user who
        // abandons the OS prompt is already stamped, so they are never sent
        // back through the blocking conversation to reach their feed.
        // A permission state we have not read yet (`null`) is asked anyway —
        // the same default as every other opt-in surface, so a still-loading
        // status can never race the ask away.
        // A desktop browser never asks: straight to the feed. A native shell
        // asks, also on a tablet that has the desktop layout.
        if (
          (isDesktopShell() && !isNativeApp()) ||
          (permissionState !== null &&
            permissionState.status !== "notDetermined")
        ) {
          history.replace("/discovery");
          return;
        }
        setPushOptInPending(true);
      } catch {
        displayToast(t("Something went wrong. Please try again."), "error");
      }
    });
  }

  return (
    <div className="flex h-dvh flex-col bg-ds-cream pt-(--safe-area-top)">
      <div className="flex justify-end px-3.5 py-3">
        <button
          type="button"
          onClick={close}
          aria-label={t("Close")}
          className="flex size-8 items-center justify-center rounded-full bg-ds-white"
        >
          <IconX className="size-5 text-ds-mint-900" />
        </button>
      </div>
      <div className="relative min-h-0 flex-1">
        <ProfilingConversation
          headerInset={0}
          specialtyId={null}
          roomCount={currentUser === null ? 0 : currentUser.rooms.length}
          finishing={terminalAction.state.status === "running"}
          onFinish={() => {
            void finish();
          }}
        />
      </div>
      <PushOptInModal
        visible={pushOptInPending}
        surface="profiling_completed"
        onDone={() => {
          setPushOptInPending(false);
          history.replace("/discovery");
        }}
      />
    </div>
  );
}
